import axios from "axios";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ListGroup from "react-bootstrap/ListGroup";
import Card from "react-bootstrap/Card";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import { Store } from "../Store";
import { getError } from "../utils";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import Form from "react-bootstrap/Form";
import VoiceRecorder from "../components/VoiceRecorder";
import { useSocket } from "../SocketContext";


function reducer(state, action) {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true, error: "" };
    case "FETCH_SUCCESS":
      return { ...state, loading: false, order: action.payload, error: "" };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false, successUpdate: true };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    case "UPDATE_RESET":
      return { ...state, loadingUpdate: false, successUpdate: false };
    case "FEEDBACK_REQUEST":
      return { ...state, loadingFeedback: true };
    case "FEEDBACK_SUCCESS":
      return { ...state, loadingFeedback: false, successFeedback: true };
    case "FEEDBACK_FAIL":
      return { ...state, loadingFeedback: false };
    case "FEEDBACK_RESET":
      return { ...state, loadingFeedback: false, successFeedback: false };
    default:
      return state;
  }
}

export default function OrderScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const params = useParams();
  const { id: orderId } = params;
  const navigate = useNavigate();
  const { socket } = useSocket();


  const [
    {
      loading,
      error,
      order,
      successUpdate,
      loadingUpdate,
      loadingFeedback,
      successFeedback,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    order: {},
    error: "",
  });

  const [comment, setComment] = useState("");
  const [showRecorder, setShowRecorder] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [uploadedImage, setUploadedImage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        dispatch({ type: "FETCH_REQUEST" });
        const { data } = await axios.get(`/api/orders/${orderId}`, {
          headers: userInfo
            ? { authorization: `Bearer ${userInfo.token}` }
            : {},
        });
        dispatch({ type: "FETCH_SUCCESS", payload: data });
      } catch (err) {
        dispatch({ type: "FETCH_FAIL", payload: getError(err) });
      }
    };

    // if (!userInfo) {
    //   return navigate("/signin");
    // }
    if (
      !order._id ||
      successUpdate ||
      successFeedback ||
      (order._id && order._id !== orderId)
    ) {
      fetchOrder();
      if (successUpdate) {
        dispatch({ type: "UPDATE_RESET" });
      }
      if (successFeedback) {
        dispatch({ type: "FEEDBACK_RESET" });
      }
    }

    if (socket) {
        socket.on('new_feedback', ({ orderId: incomingOrderId, feedback }) => {
            if (incomingOrderId === orderId) {
                toast.info("New message from restaurant!");
                // Trigger a re-fetch or update locally. Re-fetching is easier for now to get full updated order object.
                dispatch({ type: 'FEEDBACK_SUCCESS' }); 
            }
        });
        return () => socket.off('new_feedback');
    }
  }, [order, userInfo, orderId, navigate, successUpdate, successFeedback, socket]);


  const updateStatusHandler = async (newStatus) => {
    try {
      dispatch({ type: "UPDATE_REQUEST" });
      await axios.put(
        `/api/orders/${order._id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      dispatch({ type: "UPDATE_SUCCESS" });
      toast.success(`Order marked as ${newStatus}`);
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "UPDATE_FAIL" });
    }
  };

  const uploadFileHandler = async (file) => {
    if (!file) return null;
    try {
      setIsUploading(true);
      const bodyFormData = new FormData();

      // For audio blobs, we need to explicitly provide a filename
      if (file instanceof Blob && !(file instanceof File)) {
        const extension = file.type.includes("webm")
          ? "webm"
          : file.type.includes("ogg")
          ? "ogg"
          : file.type.includes("wav")
          ? "wav"
          : "bin";
        bodyFormData.append(
          "file",
          file,
          `audio_record_${Date.now()}.${extension}`
        );
      } else {
        bodyFormData.append("file", file);
      }

      const { data } = await axios.post("/api/upload", bodyFormData, {
        headers: {
          ...(userInfo ? { Authorization: `Bearer ${userInfo.token}` } : {}),
        },
      });
      setIsUploading(false);
      return data.secure_url;
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(getError(err));
      setIsUploading(false);
      return null;
    }
  };

  const submitFeedbackHandler = async (e) => {
    e.preventDefault();
    if (!comment && !recordedAudio && !uploadedImage) {
      toast.error("Please provide some feedback (text, voice, or image)");
      return;
    }

    try {
      dispatch({ type: "FEEDBACK_REQUEST" });

      let voiceUrl = "";
      if (recordedAudio) {
        voiceUrl = await uploadFileHandler(recordedAudio);
      }

      await axios.post(
        `/api/orders/${order._id}/feedback`,
        {
          comment,
          voiceNote: voiceUrl,
          image: uploadedImage,
        },
        {
          headers: userInfo
            ? { Authorization: `Bearer ${userInfo.token}` }
            : {},
        }
      );
      dispatch({ type: "FEEDBACK_SUCCESS" });
      toast.success("Feedback submitted successfully");
      setComment("");
      setRecordedAudio(null);
      setUploadedImage("");
      setShowRecorder(false);
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "FEEDBACK_FAIL" });
    }
  };

  return loading ? (
    <LoadingBox></LoadingBox>
  ) : error ? (
    <MessageBox variant="danger">{error}</MessageBox>
  ) : (
    <div>
      <Helmet>
        <title>Order {orderId}</title>
      </Helmet>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Order Details</h1>
        <div className="d-flex align-items-center">
          <span
            className={`badge badge-status status-${
              order.orderStatus?.toLowerCase() || "pending"
            } me-2`}
          >
            {order.orderStatus || "Pending"}
          </span>
          {!userInfo && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate("/signin")}
              >
                Login
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate("/")}>
                Scan Again
              </Button>
            </div>
          )}
        </div>
      </div>

      <Row>
        <Col md={8}>
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Location Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p className="mb-1 text-muted">
                    {order.locationType === "room" ? "Room" : "Table"} Number
                  </p>
                  <h4>{order.tableNumber || "N/A"}</h4>
                </Col>
                <Col md={6}>
                  <p className="mb-1 text-muted">Customer Name</p>
                  <h4>{order.customerName || order.user?.name || "Guest"}</h4>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Order Items</h5>
            </Card.Header>
            <ListGroup variant="flush">
              {order.orderItems.map((item) => (
                <ListGroup.Item key={item._id} className="py-3">
                  <Row className="align-items-center">
                    <Col md={6} className="d-flex align-items-center">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="rounded"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          marginRight: "15px",
                        }}
                      />
                      <div>
                        <div className="fw-bold">{item.name}</div>
                        {item.specialInstructions && (
                          <div className="text-muted small fst-italic">
                            Note: {item.specialInstructions}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={3} className="text-center">
                      <span className="badge bg-light text-dark border">
                        Qty: {item.quantity}
                      </span>
                    </Col>
                    <Col md={3} className="text-end fw-bold">
                      {(item.price * item.quantity).toFixed(0)} RWF
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Payment Summary</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item className="d-flex justify-content-between">
                <span>Subtotal</span>
                <span>{order.itemsPrice?.toFixed(0)} RWF</span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between">
                <span>Tax</span>
                <span>{order.taxPrice?.toFixed(0)} RWF</span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between bg-light fw-bold">
                <span>Total</span>
                <span>{order.totalPrice?.toFixed(0)} RWF</span>
              </ListGroup.Item>
              <ListGroup.Item>
                <div className="d-grid gap-2">
                  <div
                    className={`alert ${
                      order.isPaid ? "alert-success" : "alert-warning"
                    } text-center mb-0 p-2`}
                  >
                    {order.isPaid
                      ? `Paid at ${order.paidAt?.substring(0, 10)}`
                      : "Payment Pending"}
                  </div>
                </div>
              </ListGroup.Item>
            </ListGroup>
          </Card>

          {userInfo && userInfo.isAdmin && (
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">Kitchen Actions</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  {order.orderStatus === "Pending" && (
                    <Button
                      variant="primary"
                      onClick={() => updateStatusHandler("Preparing")}
                    >
                      Start Preparing
                    </Button>
                  )}
                  {order.orderStatus === "Preparing" && (
                    <Button
                      variant="success"
                      onClick={() => updateStatusHandler("Ready")}
                    >
                      Mark Ready
                    </Button>
                  )}
                  {order.orderStatus === "Ready" && (
                    <Button
                      variant="info"
                      onClick={() => updateStatusHandler("Served")}
                    >
                      Mark Served
                    </Button>
                  )}
                  {loadingUpdate && <LoadingBox />}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Claims & Feedback</h5>
            </Card.Header>
            <Card.Body>
              {order.feedbacks && order.feedbacks.length > 0 ? (
                <div
                  className="feedback-history mb-4"
                  style={{
                    maxHeight: "500px",
                    overflowY: "auto",
                    padding: "10px",
                  }}
                >
                  {order.feedbacks.map((f, i) => {
                    const getDownloadUrl = (url) => {
                      if (!url) return "";
                      // Firebase Storage URLs don't need the Cloudinary fl_attachment transformation.
                      return url;
                    };

                    return (
                      <div key={i} className="mb-4">
                        {/* Customer Message bubble (only if not manager initiated) */}
                        {!f.isManagerInitiated && (
                          <div className="d-flex flex-column align-items-start mb-3">
                            <div
                              className="message-bubble customer-msg p-3 rounded"
                              style={{
                                backgroundColor: "#fff3cd",
                                maxWidth: "85%",
                                borderLeft: "4px solid #ffc107",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="fw-bold text-muted">You</small>
                                <small
                                  className="text-muted ms-3"
                                  style={{ fontSize: "0.7em" }}
                                >
                                  {new Date(f.createdAt).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </small>
                              </div>
                              {f.comment && (
                                <div className="mb-2">{f.comment}</div>
                              )}
                              {f.image && (
                                <div className="mb-2 position-relative d-inline-block">
                                  <img
                                    src={f.image}
                                    alt="Attachment"
                                    className="img-thumbnail mb-2"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "200px",
                                    }}
                                  />
                                  <a
                                    href={getDownloadUrl(f.image)}
                                    download
                                    className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm"
                                    title="Download"
                                  >
                                    <i className="fas fa-download text-dark"></i>
                                  </a>
                                </div>
                              )}
                              {f.voiceNote && (
                                <div className="mt-1 d-flex align-items-center gap-2">
                                  <audio
                                    src={f.voiceNote}
                                    controls
                                    style={{ width: "100%", height: "45px" }}
                                  />
                                  <a
                                    href={getDownloadUrl(f.voiceNote)}
                                    download
                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                    title="Download"
                                  >
                                    <i className="fas fa-download"></i>
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Manager's Message bubble */}
                        {(f.reply || f.replyVoiceNote || f.replyImage) ? (
                          <div className="d-flex flex-column align-items-end mb-3">
                            <div
                              className="message-bubble manager-msg p-3 rounded"
                              style={{
                                backgroundColor: "#d1e7dd",
                                maxWidth: "85%",
                                borderRight: "4px solid #198754",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small
                                  className="text-muted me-3"
                                  style={{ fontSize: "0.7em" }}
                                >
                                  {new Date(f.repliedAt || f.createdAt).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </small>
                                <small className="fw-bold text-muted">
                                  Manager
                                </small>
                              </div>
                              {f.reply && <div className="mb-2">{f.reply}</div>}
                              {f.replyImage && (
                                <div className="mb-2 position-relative d-inline-block">
                                  <img
                                    src={f.replyImage}
                                    alt="Manager Attachment"
                                    className="img-thumbnail mb-2"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "200px",
                                    }}
                                  />
                                  <a
                                    href={getDownloadUrl(f.replyImage)}
                                    download
                                    className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm"
                                    title="Download"
                                  >
                                    <i className="fas fa-download text-dark"></i>
                                  </a>
                                </div>
                              )}
                              {f.replyVoiceNote && (
                                <div className="mt-1 d-flex align-items-center gap-2">
                                  <audio
                                    src={f.replyVoiceNote}
                                    controls
                                    style={{ width: "100%", height: "45px" }}
                                  />
                                  <a
                                    href={getDownloadUrl(f.replyVoiceNote)}
                                    download
                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                    title="Download"
                                  >
                                    <i className="fas fa-download"></i>
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : !f.isManagerInitiated && (
                          <div className="text-center">
                            <small className="text-muted fst-italic">
                              Waiting for manager's response...
                            </small>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <MessageBox variant="info">
                  No claims or feedback submitted yet.
                </MessageBox>
              )}

              <Form
                onSubmit={submitFeedbackHandler}
                className="mt-4 pt-3 border-top"
              >
                <Form.Group className="mb-3" controlId="comment">
                  <Form.Label className="fw-bold">
                    Raise a Claim / Follow up
                  </Form.Label>

                  {uploadedImage && (
                    <div
                      className="position-relative mb-2"
                      style={{ maxWidth: "150px" }}
                    >
                      <img
                        src={uploadedImage}
                        alt="To upload"
                        className="img-thumbnail"
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle"
                        style={{ padding: "0px 5px" }}
                        onClick={() => setUploadedImage("")}
                      >
                        &times;
                      </Button>
                    </div>
                  )}

                  {showRecorder ? (
                    <VoiceRecorder
                      onRecordingComplete={(blob, url) =>
                        setRecordedAudio(blob)
                      }
                      onCancel={() => {
                        setShowRecorder(false);
                        setRecordedAudio(null);
                      }}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Write your message here..."
                      className="mb-2"
                    ></Form.Control>
                  )}

                  <div className="d-flex gap-2 mb-3">
                    {!showRecorder && (
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => setShowRecorder(true)}
                      >
                        <i className="fas fa-microphone"></i> Record Voice
                      </Button>
                    )}

                    <label className="btn btn-outline-info btn-sm m-0">
                      <i className="fas fa-camera"></i>{" "}
                      {uploadedImage ? "Change Photo" : "Add Photo"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={async (e) => {
                          const url = await uploadFileHandler(
                            e.target.files[0]
                          );
                          if (url) setUploadedImage(url);
                        }}
                      />
                    </label>
                  </div>
                </Form.Group>
                <div className="d-grid">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={
                      loadingFeedback ||
                      isUploading ||
                      (!comment && !recordedAudio && !uploadedImage)
                    }
                  >
                    {isUploading ? "Uploading Media..." : "Send Message"}
                  </Button>
                </div>
                {loadingFeedback && <LoadingBox></LoadingBox>}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
